import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ICategory } from 'modules/inventory/settings/category/interfaces';
import { CategoryService } from 'modules/inventory/settings/category/services';

const useCategorySlugPath = (category: ICategory | undefined): string | null => {
  const { data } = useQuery({
    queryKey: ['categories-all-for-link'],
    queryFn: () => CategoryService.searchAll(),
    staleTime: 60 * 1000,
    enabled: !!category?.slug,
  });

  return useMemo(() => {
    if (!category?.slug) return null;

    const raw = data as unknown;
    const categories: ICategory[] = Array.isArray(raw) ? raw : ((raw as { data?: ICategory[] })?.data ?? []);

    const categoryMap = new Map<string, ICategory>();
    categories.forEach((cat) => {
      if (cat._id) categoryMap.set(cat._id, cat);
    });

    const slugs: string[] = [];
    let current: ICategory | undefined = category;
    const visited = new Set<string>();

    while (current) {
      if (!current._id || visited.has(current._id)) break;
      visited.add(current._id);
      if (current.slug) slugs.unshift(current.slug);
      if (!current.parent || current.parent === current._id) break;
      current = categoryMap.get(current.parent as string);
    }

    return slugs.length ? slugs.join('/') : null;
  }, [category, data]);
};

export default useCategorySlugPath;
